/**
 * Session API Service
 * Handles all API calls for user session management
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

class SessionApiService {
  /**
   * Create headers with auth token
   */
  getHeaders(token) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Create a new optimization session
   */
  async createSession(modelId, hardwareTarget, sessionName, token) {
    const response = await fetch(`${API_BASE}/api/sessions`, {
      method: 'POST',
      headers: this.getHeaders(token),
      body: JSON.stringify({
        model_id: modelId,
        hardware_target: hardwareTarget,
        session_name: sessionName
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create session');
    }

    return response.json();
  }

  /**
   * Get all sessions for the current user
   */
  async getUserSessions(token, status = null, modelId = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (modelId) params.append('model_id', modelId);

    const url = `${API_BASE}/api/sessions?${params.toString()}`;

    const response = await fetch(url, {
      headers: this.getHeaders(token)
    });

    if (!response.ok) {
      throw new Error('Failed to fetch sessions');
    }

    return response.json();
  }

  /**
   * Get a specific session by ID
   */
  async getSession(sessionId, token) {
    const response = await fetch(`${API_BASE}/api/sessions/${sessionId}`, {
      headers: this.getHeaders(token)
    });

    if (!response.ok) {
      throw new Error('Failed to fetch session');
    }

    return response.json();
  }

  /**
   * Update session metadata
   */
  async updateSession(sessionId, updates, token) {
    const response = await fetch(`${API_BASE}/api/sessions/${sessionId}`, {
      method: 'PATCH',
      headers: this.getHeaders(token),
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error('Failed to update session');
    }

    return response.json();
  }

  /**
   * Delete session (soft delete by default)
   */
  async deleteSession(sessionId, hardDelete = false, token) {
    const url = `${API_BASE}/api/sessions/${sessionId}${hardDelete ? '?hard_delete=true' : ''}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(token)
    });

    if (!response.ok) {
      throw new Error('Failed to delete session');
    }

    return response.json();
  }

  /**
   * Autosave layer code
   */
  async autosaveLayer(sessionId, layerId, code, technique = null, notes = null, token) {
    const response = await fetch(`${API_BASE}/api/sessions/${sessionId}/layers/autosave`, {
      method: 'POST',
      headers: this.getHeaders(token),
      body: JSON.stringify({
        layer_id: layerId,
        code: code,
        optimization_technique: technique,
        notes: notes
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to autosave layer');
    }

    return response.json();
  }

  /**
   * Save execution results for a layer
   */
  async saveExecutionResult(sessionId, layerId, status, output = null, metrics = null, token) {
    const response = await fetch(`${API_BASE}/api/sessions/${sessionId}/layers/${layerId}/execution`, {
      method: 'POST',
      headers: this.getHeaders(token),
      body: JSON.stringify({
        layer_id: layerId,
        status: status,
        output: output,
        metrics: metrics
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save execution result');
    }

    return response.json();
  }

  /**
   * Get all layers in a session
   */
  async getSessionLayers(sessionId, modifiedOnly = false, token) {
    const url = `${API_BASE}/api/sessions/${sessionId}/layers${modifiedOnly ? '?modified_only=true' : ''}`;

    const response = await fetch(url, {
      headers: this.getHeaders(token)
    });

    if (!response.ok) {
      throw new Error('Failed to fetch session layers');
    }

    return response.json();
  }

  /**
   * Get specific layer data in session
   */
  async getSessionLayer(sessionId, layerId, token) {
    const response = await fetch(`${API_BASE}/api/sessions/${sessionId}/layers/${layerId}`, {
      headers: this.getHeaders(token)
    });

    if (!response.ok) {
      throw new Error('Failed to fetch session layer');
    }

    return response.json();
  }

  /**
   * Check for active session for recovery
   */
  async findActiveSession(modelId, token) {
    const response = await fetch(`${API_BASE}/api/sessions/recovery/${modelId}`, {
      headers: this.getHeaders(token)
    });

    if (!response.ok) {
      throw new Error('Failed to check for active session');
    }

    return response.json();
  }

  /**
   * Export complete session data
   */
  async exportSession(sessionId, token) {
    const response = await fetch(`${API_BASE}/api/sessions/${sessionId}/export`, {
      headers: this.getHeaders(token)
    });

    if (!response.ok) {
      throw new Error('Failed to export session');
    }

    return response.json();
  }

  // Topology analysis method removed - deprecated feature

  /**
   * Export session as Python package
   * Returns session data, model data, and layers for client-side ZIP generation
   */
  async exportSessionAsPython(sessionId, modelId, token) {
    try {
      // Use new endpoint that includes function_code
      const exportResponse = await fetch(`${API_BASE}/api/sessions/${sessionId}/export-with-code`, {
        headers: this.getHeaders(token)
      });

      if (!exportResponse.ok) {
        throw new Error('Failed to export session with code');
      }

      const exportData = await exportResponse.json();

      // Fetch model metadata separately
      const modelResponse = await fetch(`${API_BASE}/api/models/${modelId}`, {
        headers: this.getHeaders(token)
      });

      if (!modelResponse.ok) {
        throw new Error('Failed to fetch model data');
      }

      const model = await modelResponse.json();

      return {
        session: exportData.session,
        model: model.model || model,  // Handle nested model structure
        layers: exportData.layers  // Now includes function_code and current_code
      };
    } catch (error) {
      console.error('Failed to export session as Python:', error);
      throw error;
    }
  }

  async getLayerIOMetadata(sessionId, layerId, accessToken) {
    /**
     * Get I/O metadata for a layer.
     * Returns both golden (immutable reference) and session (mutable experimental) metadata.
     */
    try {
      const response = await fetch(
        `${API_BASE}/api/sessions/${sessionId}/layers/${layerId}/io-metadata`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get I/O metadata: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get layer I/O metadata:', error);
      throw error;
    }
  }

  async updateSessionIOMetadata(sessionId, layerId, inputMetadata, outputMetadata, accessToken) {
    /**
     * Update session-level I/O metadata (experimental/mutable, NOT golden).
     * Golden metadata is immutable and used for validation.
     */
    try {
      const response = await fetch(
        `${API_BASE}/api/sessions/${sessionId}/layers/${layerId}/io-metadata`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            input_metadata: inputMetadata,
            output_metadata: outputMetadata
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update I/O metadata: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update session I/O metadata:', error);
      throw error;
    }
  }

  async mergeLayers(sessionId, layer1Id, layer2Id, feedToInput, accessToken) {
    /**
     * Merge two consecutive layers using AST-based function merging.
     *
     * @param {string} sessionId - Session UUID
     * @param {string} layer1Id - First layer UUID
     * @param {string} layer2Id - Second layer UUID
     * @param {number[]} feedToInput - Input positions where func1 output feeds to func2 (0-indexed)
     * @param {string} accessToken - Auth token
     * @returns {Promise<Object>} Merge result with new merged layer info
     */
    try {
      const response = await fetch(
        `${API_BASE}/api/sessions/${sessionId}/merge-layers`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            layer1_id: layer1Id,
            layer2_id: layer2Id,
            feed_to_input: feedToInput
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to merge layers: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to merge layers:', error);
      throw error;
    }
  }

  async executePytest(sessionId, layerId, device = 'cpu', accessToken) {
    /**
     * Execute pytest for a specific layer (correctness testing).
     * Creates a Redis job to test the user's code against the golden baseline.
     *
     * @param {string} sessionId - Session UUID
     * @param {string} layerId - Layer UUID being tested
     * @param {string} device - Execution device ('cpu' or 'cuda'), defaults to 'cpu'
     * @param {string} accessToken - Auth token
     * @returns {Promise<Object>} Job information with job_id for tracking
     */
    try {
      const response = await fetch(
        `${API_BASE}/api/sessions/${sessionId}/layers/${layerId}/pytest/execute`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ device })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to execute pytest: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to execute pytest:', error);
      throw error;
    }
  }
}

// Export singleton instance
const sessionApiService = new SessionApiService();
export default sessionApiService;
